<template>
  <div class="cond-row" :class="{ 'cond-row--error': hasError }">
    <span class="cond-no">{{ index + 1 }}</span>
    <div class="flex-1 min-w-0 flex flex-col gap-2">
      <QuestionPicker :model-value="state.question" :questions="questions" :allowed-scopes="allowedScopes" @update:modelValue="onQuestion" />

      <div v-if="question" class="flex flex-col md:flex-row md:items-start gap-2">
        <select :value="uiOp" class="form-select md:w-52 flex-shrink-0" aria-label="เปรียบเทียบคำตอบ" @change="onUiOp($event.target.value)">
          <option v-for="o in opOptions" :key="o.value" :value="o.value">{{ o.label }}</option>
        </select>

        <div class="flex-1 min-w-0">
          <div v-if="!needsValue" class="text-sm text-theme-37 py-2">ไม่ต้องเลือกคำตอบ</div>

          <!-- coded answers: click the answers that count -->
          <template v-else-if="hasChoices">
            <input v-if="question.choices.length > 10" v-model.trim="choiceSearch" type="text" class="form-control form-control-sm py-1 mb-1.5 text-sm" placeholder="พิมพ์เพื่อค้นหาคำตอบ…">
            <div class="flex flex-wrap gap-1.5 max-h-60 overflow-y-auto">
              <button v-for="ch in visibleChoices" :key="ch.code" type="button" class="answer-chip" :class="{ 'answer-chip--on': selectedCodes.includes(ch.code) }" :title="ch.code" @click="toggleChoice(ch.code)">
                <CheckIcon v-if="selectedCodes.includes(ch.code)" class="w-3 h-3 flex-shrink-0" />{{ ch.desc }}
              </button>
              <!-- a stored value that is not one of the question's answers (old seed data): show it, never drop it silently -->
              <button v-for="v in unknownCodes" :key="'u' + String(v)" type="button" class="answer-chip answer-chip--unknown" title="กดเพื่อเอาค่านี้ออก" @click="toggleChoice(v)">
                <AlertTriangleIcon class="w-3 h-3 flex-shrink-0" />{{ String(v) }} <XIcon class="w-3 h-3 flex-shrink-0" />
              </button>
              <span v-if="!visibleChoices.length && !unknownCodes.length" class="text-xs text-theme-37 py-1">ไม่พบคำตอบที่ค้นหา</span>
            </div>
            <div v-if="unknownCodes.length" class="text-xs mt-1" style="color:#B36B00">ค่าที่บันทึกไว้ ({{ unknownCodes.map(String).join(", ") }}) ไม่ตรงกับตัวเลือกใดของคำถามนี้ เงื่อนไขนี้จึงอาจไม่เคยตรงกับครัวเรือนใดเลย — คลิกคำตอบที่ถูกต้องแล้วเอาค่าเดิมออก</div>
            <div class="text-xs text-theme-37 mt-1">{{ multiChoice ? "เลือกได้หลายคำตอบ — ตรงคำตอบใดคำตอบหนึ่งก็นับ" : "เลือกได้ 1 คำตอบ (ถ้าต้องการหลายตัวเลือก ให้เพิ่มเงื่อนไขอีกข้อ)" }}</div>
          </template>

          <select v-else-if="isBoolean" v-model="state.value" class="form-select" @change="afterValue">
            <option :value="true">ใช่</option>
            <option :value="false">ไม่ใช่</option>
          </select>
          <input v-else-if="isArrayOp" v-model="valueText" type="text" class="form-control" placeholder="ใส่ได้หลายค่า คั่นด้วยเครื่องหมายจุลภาค ," @change="afterValue">
          <input v-else v-model="state.value" :type="isNumeric ? 'number' : 'text'" step="any" class="form-control" :placeholder="isNumeric ? 'ตัวเลข เช่น 100000' : 'ข้อความ'" @input="afterValue">
        </div>
      </div>

      <!-- evidence sentence shown on the household page; written automatically, editable on demand -->
      <div v-if="question" class="text-xs text-theme-37">
        <div v-if="!editDesc" class="flex items-start gap-1">
          <span class="flex-shrink-0">ข้อความที่แสดงบนหน้าครัวเรือน:</span>
          <span class="text-gray-700 break-words min-w-0">{{ state.value_desc_th || "—" }}</span>
          <button type="button" class="underline flex-shrink-0 ml-1" @click="editDesc = true">แก้ข้อความ</button>
          <button v-if="descStale" type="button" class="underline flex-shrink-0 ml-1" style="color:#B36B00" title="ข้อความนี้เขียนไว้เองก่อนเปลี่ยนคำตอบ กดเพื่อให้ระบบเขียนใหม่ตามคำตอบล่าสุด" @click="resetDesc">คำตอบเปลี่ยนแล้ว · เขียนข้อความใหม่ให้</button>
        </div>
        <div v-else class="flex items-center gap-2">
          <input v-model.trim="state.value_desc_th" type="text" class="form-control form-control-sm text-sm py-1" placeholder="ข้อความสั้น ๆ ที่แสดงเป็นหลักฐานบนหน้าครัวเรือน" @input="descEdited = true">
          <button type="button" class="underline flex-shrink-0" title="กลับไปใช้ข้อความที่ระบบเขียนให้" @click="resetDesc">ใช้ข้อความอัตโนมัติ</button>
        </div>
      </div>
      <div v-if="hasError" class="field-error mt-0">{{ error }}</div>
    </div>

    <div class="flex flex-col items-center gap-1 flex-shrink-0">
      <button type="button" class="icon-btn icon-btn-plain" title="ลบเงื่อนไขนี้" @click="$emit('remove')"><XIcon class="w-4 h-4" /></button>
      <button v-if="moveTitle" type="button" class="icon-btn icon-btn-plain" :title="moveTitle" @click="$emit('move')"><RepeatIcon class="w-3.5 h-3.5" /></button>
    </div>
  </div>
</template>

<script setup>
import { computed, reactive, ref, watch } from "vue";
import QuestionPicker from "../components/QuestionPicker.vue";
import { OP_LABEL, OPS_NO_VALUE, OPS_ARRAY_VALUE } from "../constants";

const props = defineProps({
  modelValue: { type: Object, required: true },  // condition { question, op, value, field, value_desc_th }
  index: { type: Number, default: 0 },
  questions: { type: Array, default: () => [] },
  allowedScopes: { type: Array, default: null },
  error: { type: String, default: "" },
  moveTitle: { type: String, default: "" },      // set in the mixed (AND + OR) layout: shows a "move to the other group" button
});
const emit = defineEmits(["update:modelValue", "remove", "move"]);

// local editable copy; every change is emitted so the parent's list stays the source of truth
const state = reactive({ question: "", op: "eq", value: "", field: undefined, value_desc_th: "", ...props.modelValue });
let syncing = false;
watch(() => props.modelValue, (v) => {
  if (JSON.stringify(v) === JSON.stringify(plain())) return;
  syncing = true;
  Object.assign(state, { question: "", op: "eq", value: "", field: undefined, value_desc_th: "", ...v });
  syncing = false;
}, { deep: true });
watch(state, () => { if (!syncing) emit("update:modelValue", plain()); }, { deep: true });
const plain = () => {
  const out = { question: state.question, op: state.op, value_desc_th: state.value_desc_th };
  if (state.field) out.field = state.field;
  if (!OPS_NO_VALUE.includes(state.op)) out.value = state.value;
  return out;
};

const byCode = computed(() => Object.fromEntries(props.questions.map((q) => [q.code, q])));
const question = computed(() => byCode.value[state.question] || null);
const ops = computed(() => (question.value && question.value.ops && question.value.ops.length ? question.value.ops : Object.keys(OP_LABEL)));
const needsValue = computed(() => !OPS_NO_VALUE.includes(state.op));
const isArrayOp = computed(() => OPS_ARRAY_VALUE.includes(state.op));
const hasChoices = computed(() => !!(question.value && question.value.choices && question.value.choices.length));
const isBoolean = computed(() => !!question.value && question.value.kind === "derived" && /^hh\.has_/.test(question.value.code));
const isNumeric = computed(() => !!question.value && (question.value.type === "number" || question.value.kind === "derived") && !isBoolean.value);
const hasError = computed(() => !!props.error);

// ---- operator, in plain words. "ตอบว่า" covers eq (1 answer) and in (several); the chips decide which ----
const multiChoice = computed(() => hasChoices.value && ops.value.includes("in"));
const FAMILY = { in: "eq", not_in: "ne" };
const NO_VALUE_LABEL = { exists: "มีคำตอบ (ตอบอะไรก็ได้)", is_empty: "ไม่ได้ตอบ / เว้นว่าง" };
const uiOp = computed(() => (multiChoice.value && FAMILY[state.op]) || state.op);
const opOptions = computed(() => {
  const label = (op) => {
    if (hasChoices.value) return { eq: "ตอบว่า", ne: "ไม่ได้ตอบว่า", contains: "เลือกตัวเลือก" }[op] || NO_VALUE_LABEL[op] || OP_LABEL[op] || op;
    if (isNumeric.value) return { eq: "เท่ากับ (=)", ne: "ไม่เท่ากับ (≠)", gt: "มากกว่า (>)", gte: "ตั้งแต่ค่านี้ขึ้นไป (≥)", lt: "น้อยกว่า (<)", lte: "ไม่เกินค่านี้ (≤)" }[op] || NO_VALUE_LABEL[op] || OP_LABEL[op] || op;
    return { contains: "มีคำว่า" }[op] || NO_VALUE_LABEL[op] || OP_LABEL[op] || op;
  };
  const list = multiChoice.value ? ops.value.filter((op) => !FAMILY[op]) : ops.value;
  return list.map((op) => ({ value: op, label: label(op) }));
});
// ---- answers ----
const choiceSearch = ref("");
const visibleChoices = computed(() => {
  const s = choiceSearch.value.toLowerCase();
  return (question.value ? question.value.choices : []).filter((ch) => !s || `${ch.code} ${ch.desc}`.toLowerCase().includes(s));
});
const selectedCodes = computed(() => (Array.isArray(state.value) ? state.value : state.value === "" || state.value == null ? [] : [state.value]));
const unknownCodes = computed(() => (hasChoices.value ? selectedCodes.value.filter((v) => !question.value.choices.some((ch) => ch.code === v)) : []));
const valueText = computed({
  get: () => (Array.isArray(state.value) ? state.value.join(", ") : state.value || ""),
  set: (v) => { state.value = String(v).split(",").map((s) => s.trim()).filter(Boolean); },
});

/** one answer -> eq / ne, several -> in / not_in (questions that allow it) */
function setCodes(codes, family = uiOp.value) {
  if (!multiChoice.value || !["eq", "ne"].includes(family)) { state.value = codes.length ? codes[codes.length - 1] : ""; return; }
  if (codes.length > 1) { state.op = family === "eq" ? "in" : "not_in"; state.value = codes; }
  else { state.op = family; state.value = codes[0] || ""; }
}
function toggleChoice(code) {
  const cur = selectedCodes.value;
  setCodes(cur.includes(code) ? cur.filter((c) => c !== code) : [...cur, code]);
  afterValue();
}

function onQuestion(code) {
  state.question = code;
  const q = byCode.value[code];
  state.op = q && q.ops && q.ops.length ? (q.ops.includes("eq") ? "eq" : q.ops[0]) : "eq";
  state.value = isBoolean.value ? true : "";
  state.field = q ? String(code).toUpperCase() : undefined;
  choiceSearch.value = "";
  descEdited.value = false; descStale.value = false;
  state.value_desc_th = autoDesc();
}
function onUiOp(op) {
  const codes = selectedCodes.value;
  if (OPS_NO_VALUE.includes(op)) { state.op = op; state.value = ""; }
  else if (hasChoices.value) { state.op = op; setCodes(codes, op); }
  else {
    state.op = op;
    if (OPS_ARRAY_VALUE.includes(op) && !Array.isArray(state.value)) state.value = state.value === "" ? [] : [state.value];
    else if (!OPS_ARRAY_VALUE.includes(op) && Array.isArray(state.value)) state.value = state.value[0] || "";
  }
  afterValue();
}

// ---- evidence text: written for the admin until they edit it themselves ----
function autoDesc() {
  const q = question.value;
  if (!q) return "";
  const opLabel = OP_LABEL[state.op] || state.op;
  if (!needsValue.value) return `${q.desc} ${opLabel}`;
  if (hasChoices.value) {
    const descs = selectedCodes.value.map((c) => (q.choices.find((ch) => ch.code === c) || {}).desc).filter(Boolean).join(" / ");
    return ["ne", "not_in"].includes(state.op) && descs ? `${q.desc} ไม่ใช่ ${descs}` : descs;
  }
  if (isBoolean.value) return state.value === false ? `ไม่ใช่: ${q.desc}` : q.desc;
  const v = Array.isArray(state.value) ? state.value.join(", ") : state.value;
  return `${q.desc} ${opLabel} ${v ?? ""}`.trim();
}
const editDesc = ref(false);
const descEdited = ref(!!state.value_desc_th && !!question.value && state.value_desc_th !== autoDesc());
const descStale = ref(false);   // a hand-written text is kept when the answers change, so point it out
function afterValue() {
  if (!descEdited.value) state.value_desc_th = autoDesc();
  else descStale.value = state.value_desc_th !== autoDesc();
}
function resetDesc() { descEdited.value = false; descStale.value = false; editDesc.value = false; state.value_desc_th = autoDesc(); }
</script>

<style scoped>
.cond-row { display: flex; align-items: flex-start; gap: 0.6rem; border: 1px solid #e2e8f0; border-radius: 0.5rem; background: #fff; padding: 0.65rem; }
.cond-row--error { border-color: #CE3131; }
.cond-no { width: 1.5rem; height: 1.5rem; margin-top: 0.45rem; border-radius: 9999px; background: #eef1f6; color: #415A77; font-size: 0.75rem; font-weight: 600; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.answer-chip { display: inline-flex; align-items: center; gap: 0.25rem; max-width: 100%; text-align: left; font-size: 0.8rem; line-height: 1.25; padding: 0.3rem 0.7rem; border-radius: 0.9rem; border: 1px solid #dce1ea; background: #fff; color: #4a5568; transition: background-color 0.12s ease, border-color 0.12s ease; }
.answer-chip:hover { border-color: #415A77; }
.answer-chip--on { background: #415A77; border-color: #415A77; color: #fff; }
.answer-chip--unknown { background: #fdf3df; border-color: #f0dcae; color: #8a5a00; }
</style>
